import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, map, switchMap, finalize, share, filter, tap, merge, catchError, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AuthHttpService } from './auth-http.service';
import { CookieService } from 'ngx-cookie-service';

import { AuthModel, AuthenticationResponseModelData, AuthenticationResponseModel, LinkedUserResponseModel, LinkedUserAuthModel } from '../models/auth.model';
import { TenantModel, UserModel, UserModelData } from '../models/user.model';
import { ForgotPasswordRequest, ResetPasswordRequest, ResetPasswordResponseModelData, ResetPasswordResponseModel, ResolveTenantRequest } from '../models/passwords.model';
import { AppLoggingService } from 'src/app/_shared/core/services/app-logging.service';
import { StsHttpService } from './sts-http.service';
import { UserSettingService } from 'src/app/main/core/services/user-setting.service';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // public fields
  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  isLoadingSubject: BehaviorSubject<boolean>;

  currentListOfTenants$: Observable<LinkedUserResponseModel[]>;
  currentListOfTenantsSubject: BehaviorSubject<LinkedUserResponseModel[]>;
  private _pendingLinkedUsers: LinkedUserResponseModel[] = [];
  private _ssoLoginError: string = '';

  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user);
  }

  get isAuthenticated(): boolean {
    return this.cookieService.check(environment.tokenKey);
  }

  get token(): string | null {
    return this.cookieService.get(environment.tokenKey);
  }
  get hubToken(): string | null {
    return this.cookieService.get(environment.hubTokenKey);
  }

  get currentTenantIdValue(): number | null {
    return +this.cookieService.get(environment.tenantIdKey);
  }

  set currentTenantIdValue(tenantId: number) {
    this.setTenantInCookie(tenantId);
  }

  get currentListOfTenantsValue(): LinkedUserResponseModel[] {
    this.currentUser$.subscribe(user => {
      if (user.isImpersonated) {
        this.cookieService.delete(environment.linkedUserKey, '/');
        this.currentListOfTenantsSubject.next([])
      }
    })
    const linkedUsers = this.currentListOfTenantsSubject.value;
    if (linkedUsers?.length > 0) {
      return linkedUsers;
    } else {
      const linkedUserFromCookie = this.cookieService.get(environment.linkedUserKey) ? JSON.parse(this.cookieService.get(environment.linkedUserKey)) : [];
      this.currentListOfTenantsSubject.next(linkedUserFromCookie);
      return linkedUserFromCookie;
    }
  }

  set currentListOfTenantsValue(linkedUser: LinkedUserResponseModel[]) {
    this.currentListOfTenantsSubject.next(linkedUser);
  }

  constructor(
    private authHttpService: AuthHttpService,
    private readonly stsHttpService: StsHttpService,
    private cookieService: CookieService,
    private readonly appLoggingService: AppLoggingService,
    private readonly userSettingService: UserSettingService
  ) { 
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.currentListOfTenantsSubject = new BehaviorSubject<LinkedUserResponseModel[]>([]);
  }

  //public methods
  login(auth: AuthModel): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    let loginQuery = this.authHttpService.login(auth).pipe(
      map((response: AuthenticationResponseModelData) => {
        if (!response?.result) {
          throw new Error("Failed to authenticate");
        }

        return response.result;
      }),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    let resetPasswordQuery = loginQuery.pipe(
      filter((result: AuthenticationResponseModel) => result.shouldResetPassword == true),
      map((result: AuthenticationResponseModel) => {
        let user = new UserModel();
        user.id = result.userId;
        user.tenant = new TenantModel();
        user.tenant.id = result.tenantId;
        user.passwordResetCode = result.passwordResetCode;
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    )

    let authenticatedQuery = loginQuery.pipe(
      filter((result: AuthenticationResponseModel) => result.shouldResetPassword == false),
      tap((result: AuthenticationResponseModel) => {
        if (!result.accessToken) {
          throw new Error("Failed to authenticate");
        }

        this.setAuthInCookie(result);
      }),
      switchMap(() => this.getUser()),
      finalize(() => this.isLoadingSubject.next(false))
    );

    return merge(resetPasswordQuery, authenticatedQuery);
  }

  loginAsTenantUser(auth: LinkedUserAuthModel): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    let loginQuery = this.authHttpService.loginAsTenantUser(auth).pipe(
      map((response: AuthenticationResponseModelData) => {
        if (!response?.result) {
          throw new Error("Failed to authenticate");
        }
        return response.result;
      }),
      share()
    );

    let authenticatedQuery = loginQuery.pipe(
      tap((result: AuthenticationResponseModel) => {
        if (!result.accessToken) {
          throw new Error("Failed to authenticate");
        }

        this.setAuthInCookie(result);
      }),
      switchMap(() => this.getUser()),
      finalize(() => this.isLoadingSubject.next(false))
    );

    return merge(authenticatedQuery);
  }

  refreshToken(token: string): Observable<AuthenticationResponseModel> {
    return this.authHttpService.refreshToken(token).pipe(
      map((response: AuthenticationResponseModelData) => {
        if (!response?.result) {
          throw new Error("Failed to reauthenticate");
        }
        this.setAuthInCookie(response.result);
        return response.result;
      })
    );
  }

  getLinkedUser(parentId: number = 0, isHubUser: boolean | null = null ): Observable<LinkedUserResponseModel[]> {
    return this.authHttpService.getLinkedUsers(parentId,isHubUser).pipe(
      map((response: LinkedUserResponseModel[]) => {
        if (!response || response.length === 0) {
          throw new Error("Failed to get Linked Users");
        }
        return response;
      })
    );
  }

  callSts(azureToken: string, partnerId: number): Observable<UserModel> {
    this.isLoadingSubject.next(true);
  
    return this.stsHttpService.callSts(azureToken, partnerId).pipe(
      map((response: AuthenticationResponseModelData) => {
        if (!response?.result?.accessToken) {
          throw new Error("Failed to authenticate");
        }
        return response.result;
      }),
      filter((result: AuthenticationResponseModel) => result != null),
      tap((result: AuthenticationResponseModel) => {
        this.setAuthInCookie(result);
      }),
      map((result: AuthenticationResponseModel) => {
        const user = new UserModel();
        user.id = result.userId;
        if (result.tenantId) {
          user.tenant = new TenantModel();
          user.tenant.id = result.tenantId;
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  tokenAuth(oktaToken: string, partnerId: number): Observable<UserModel> {
    this.isLoadingSubject.next(true);
  
    return this.stsHttpService.callOAuth(oktaToken, partnerId).pipe(
      map((response: AuthenticationResponseModelData) => {
        if (!response?.result?.accessToken) {
          throw new Error("Failed to authenticate");
        }
        return response.result;
      }),
      filter((result: AuthenticationResponseModel) => result != null),
      tap((result: AuthenticationResponseModel) => {
        this.setAuthInCookie(result);
      }),
      map((result: AuthenticationResponseModel) => {
        const user = new UserModel();
        user.id = result.userId;
        if (result.tenantId) {
          user.tenant = new TenantModel();
          user.tenant.id = result.tenantId;
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }


  getUserForLayoutSetting(): number {
    const hubUserId = this.cookieService.get(environment.hubUserIdKey);
    const userId = this.cookieService.get(environment.userIdKey);

    if (hubUserId) {
        return parseInt(hubUserId, 10); 
    } else {
        return parseInt(userId, 10);
    }
}

  resolveAndUpdateTenantId(request: ResolveTenantRequest): Observable<number> {
    return this.authHttpService.resolveAndUpdateTenantId(request).pipe(
      map((response: any) => {
        if (response?.result) {
          const tenantId: number = +response.result;
          this.setTenantInCookie(tenantId);

          return tenantId;
        }

        return 0;
      })
    );
  }

  // Request password reset code
  requestPasswordResetCode(request: ForgotPasswordRequest): Observable<void> {
    return this.authHttpService.requestPasswordResetCode(request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponseModel> {
    return this.authHttpService.resetPassword(request).pipe(
      map((response: ResetPasswordResponseModelData) => {
        if (!response?.result) {
          throw new Error("Failed to reset password. Please contact your administrator.");
        }

        return response.result;
      })
    );
  }

  logout() {
    // Trigger Logout API call to clear token
    this.authHttpService.logout();
    this.appLoggingService.clearUserId();
    this.userSettingService.removeUserSettingsFromStore();
    this.cookieService.delete(environment.tokenKey,'/');
    this.cookieService.delete(environment.tenantIdKey,'/');
    this.cookieService.delete(environment.userIdKey,'/');
    this.cookieService.delete(environment.hubTokenKey,'/');
    this.cookieService.delete(environment.hubUserIdKey,'/');
    this.cookieService.delete(environment.linkedUserKey,'/');
    this.cookieService.delete(environment.isSsoOnlyUser, '/');
    this.currentListOfTenantsSubject.next([]);
  }

  getUser(): Observable<UserModel> {
    if (!this.token) {
      this.currentUserSubject.next(null);
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    if (this.cookieService.check("sc-linkedUserList") && !this.cookieService.check("Abp.TenantId")) {
      this.logout();
      this.isLoadingSubject.next(false);
      return of(undefined);
    }
    
    return this.authHttpService.getUser().pipe(
      map((response: UserModelData) => {
        if (!response?.result) {
          this.currentUserSubject.next(null);
          this.logout();
          throw new Error("User cannot be found.");
        }

        this.currentUserSubject.next(response.result);
        this.appLoggingService.setUserId(response.result?.id.toString(), response.result?.tenant?.id.toString(), response.result);
        return response.result;
      }),
      catchError((error: any) => {
        this.currentUserSubject.next(null);
        this.isLoadingSubject.next(false);
        throw new Error(error);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  //private methods
  private setAuthInCookie(response: AuthenticationResponseModel): void {
    const expiry = response.expireInSeconds / 86400;
    
    this.cookieService.set(environment.tokenKey, response.accessToken, expiry,'/');
    this.cookieService.set(environment.userIdKey, response.userId.toString(), expiry,'/');
    if (response.tenantId) {
      this.setTenantInCookie(response.tenantId, expiry);
    }
    else{
      this.cookieService.set(environment.hubUserIdKey, response.userId.toString(), expiry,'/');
      this.cookieService.set(environment.hubTokenKey, response.accessToken, expiry,'/');
    }
  }

  private setTenantInCookie(tenantId: number, expiry?: number) {
    this.cookieService.set(environment.tenantIdKey, tenantId.toString(), expiry,'/');
  }

  setListOfLinkedUserInCookie(linkedUser: LinkedUserResponseModel[]) {
    this.cookieService.set(environment.linkedUserKey, JSON.stringify(linkedUser), null, '/');
  }

  updateLastLoginTime(userId: number): Observable<boolean> {
    return this.authHttpService.updateLastLoginTime(userId).pipe(
      map((response: boolean) => {
        return response;
      })
    );
  }

  get pendingLinkedUsers(): LinkedUserResponseModel[] {
    return this._pendingLinkedUsers;
  }

  setPendingLinkedUsers(users: LinkedUserResponseModel[]) {
    this._pendingLinkedUsers = users;
  }

  clearPendingLinkedUsers() {
    this._pendingLinkedUsers = [];
  }

  get ssoLoginError(): string {
    return this._ssoLoginError;
  }

  setSsoLoginError(message: string) {
    this._ssoLoginError = message;
  }

  clearSsoLoginError() {
    this._ssoLoginError = '';
  }
}
